<template>
  <svg
    ref="timelineSvg"
    class="insights-timeline-svg w-full"
    role="img"
    aria-label="Timeline visualization"
  ></svg>
</template>

<script>
import * as d3 from 'd3';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Enable timezone capabilities
dayjs.extend(utc);
dayjs.extend(timezone);

// Chart configuration constants
const CHART_CONFIG = {
  HEIGHT: 60, // Total SVG height
  BAR_HEIGHT: 38, // Height of activity bars
  MEETING_HEIGHT: 4, // Height of meeting indicators
  MEETING_RADIUS: 2, // Border radius for meeting indicators
  BAR_RADIUS: 6, // Border radius for activity bars
  Y_OFFSET_DEFAULT: 11, // Default vertical offset for activity bars (creates top margin)
  Y_OFFSET_MEETING: 52, // Vertical position for meetings (BAR_HEIGHT + margin)
  Y_OFFSET_SHIFTS: -6, // Vertical offset for shifts (negative to extend above)
  SHIFTS_HEIGHT: 60 + 2 * 6, // Height for shifts (HEIGHT + 2 * margin for top/bottom extension)
  GRID_TICK_SIZE: 60 + 1, // Height of grid tick lines (HEIGHT + 1 for border)
};

export default {
  name: 'InsightsTimelineSvg',
  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    timeData: {
      type: Array,
      required: true,
    },
    timeZone: {
      type: String,
      required: true,
    },
    svgWidth: {
      type: Number,
      required: true,
    },
    date: {
      type: String,
      required: true,
    },
    selectedTaskId: {
      type: String,
      default: null,
    },
  },
  emits: ['loaded', 'width-mismatch', 'task-clicked'],
  computed: {
    processedTimeData() {
      return this.timeData.map((d) => {
        const start = d3.isoParse(d.start_date);
        const end = d3.isoParse(d.end_date);

        return {
          ...d,
          start,
          end,
          width: (xScale) => Math.max(xScale(end) - xScale(start), 1),
          isActive: d.type === 'active',
        };
      });
    },
    today() {
      return dayjs.tz(this.date, this.timeZone);
    },
  },
  watch: {
    timeData: { handler: 'drawChart', deep: true },
    svgWidth: {
      handler: 'drawChart',
      immediate: true
    },
    selectedTaskId() {
      this.$nextTick(() => this.applySelectionState());
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.drawChart();
      this.$emit('loaded');
    });
  },
  methods: {
    drawChart() {
      const svg = d3.select(this.$refs.timelineSvg);

      svg.selectAll('*').remove();

      // Check if container width is larger than provided svgWidth
      const containerWidth = this.$refs.timelineSvg?.parentElement?.offsetWidth;

      if (containerWidth && containerWidth > this.svgWidth) {
        this.$emit('width-mismatch', containerWidth);
      }

      // Setup SVG dimensions
      svg.attr('width', this.svgWidth).attr('height', CHART_CONFIG.HEIGHT).attr('preserveAspectRatio', 'xMidYMid meet');

      // Create time scale using UTC to avoid DST issues (ensures 24 equal hours)
      const dayStart = this.today.toDate();
      const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
      const xScale = d3.scaleUtc().domain([dayStart, dayEnd]).range([0, this.svgWidth]);
      // Create timeline grid
      const xAxis = d3
        .axisBottom(xScale)
        // Hour ticks from local midnight, not UTC hours - UTC-aligned ticks drift for +05:30-style zones
        .tickValues(d3.range(25).map((h) => new Date(dayStart.getTime() + h * 60 * 60 * 1000)))
        .tickFormat('')
        .tickSize(CHART_CONFIG.GRID_TICK_SIZE);

      svg.append('g').attr('class', 'timeline-grid').attr('transform', 'translate(-1, -1)').call(xAxis);

      // Create activity gradient
      const defs = svg.append('defs');
      const gradient = defs
        .append('linearGradient')
        .attr('id', 'activity-area-gradient')
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%');

      gradient
        .append('stop')
        .attr('offset', '0%')
        .attr('stop-color', 'var(--activity-active-fill)')
        .attr('stop-opacity', '0.75');

      gradient.append('stop').attr('offset', '100%').attr('stop-color', '#000000').attr('stop-opacity', '0.05');

      // Create chart group
      const chartGroup = svg.selectAll('.user-chart').data([null]).join('g').attr('class', 'user-chart');

      this.renderActivityAreas(chartGroup, xScale);
      this.renderTasks(chartGroup, xScale);
      this.applySelectionState();
    },

    renderTasks(chartGroup, xScale) {
      const tasks = chartGroup
        .selectAll('.task')
        .data(this.processedTimeData)
        .join('g')
        .attr('class', 'task')
        .attr('transform', (d) => {
          let yPos = this.getYPosition(d.type);

          if (d.type === 'meetings') {
            const hasOverlap = this.hasMeetingOverlap(d, xScale);

            if (!hasOverlap) {
              yPos = (CHART_CONFIG.HEIGHT - CHART_CONFIG.MEETING_HEIGHT) / 2;
            }
          }

          return `translate(${xScale(d.start)}, ${yPos})`;
        });
      const rects = tasks
        .append('rect')
        .attr('class', (d) => `row-activity ${d.type}`)
        .attr('data-id', (d) => d.id)
        .attr('width', (d) => d.width(xScale))
        .attr('height', (d) => this.getHeight(d.type))
        .attr('rx', (d) => this.getRadius(d.type))
        .attr('opacity', 0.75);

      this.attachEventHandlers(rects);
    },

    renderActivityAreas(chartGroup, xScale) {
      const activeData = this.processedTimeData.filter((d) => d.isActive && d.areaData);

      chartGroup
        .selectAll('.activity-area')
        .data(activeData)
        .join('g')
        .attr('class', 'activity-area')
        .attr('transform', (d) => `translate(${xScale(d.start)}, ${CHART_CONFIG.Y_OFFSET_DEFAULT})`)
        .each((d, i, nodes) => {
          const activityGroup = d3.select(nodes[i]);
          const blockWidth = d.width(xScale);
          const taskXScale = d3.scaleTime().domain([d.start, d.end]).range([0, blockWidth]);
          const taskYScale = d3.scaleLinear().domain([0, 1]).range([CHART_CONFIG.BAR_HEIGHT, 0]);
          const areaData = d.areaData.map(({ starts_at, activity_level }) => ({
            value: activity_level,
            time: d3.isoParse(starts_at),
          }));
          const area = d3
            .area()
            .x((a) => taskXScale(a.time))
            .y0(CHART_CONFIG.BAR_HEIGHT)
            .y1((a) => taskYScale(a.value))
            .curve(d3.curveBasis);
          const line = d3
            .line()
            .x((a) => taskXScale(a.time))
            .y((a) => taskYScale(a.value))
            .curve(d3.curveBasis);
          // Create clipping path for rounded corners
          const clipId = `rounded-clip-${d.id}`;

          activityGroup
            .append('defs')
            .append('clipPath')
            .attr('id', clipId)
            .append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', blockWidth)
            .attr('height', CHART_CONFIG.BAR_HEIGHT)
            .attr('rx', CHART_CONFIG.BAR_RADIUS)
            .attr('ry', CHART_CONFIG.BAR_RADIUS);

          // Add area shape
          const areaShape = activityGroup
            .append('path')
            .datum(areaData)
            .attr('class', 'activity-area-shape')
            .attr('data-id', d.id)
            .attr('d', area)
            .attr('clip-path', `url(#${clipId})`)
            .style('opacity', 0.75);

          this.attachEventHandlers(areaShape);

          // Add line
          activityGroup
            .append('path')
            .datum(areaData)
            .attr('class', 'activity-area-line')
            .attr('fill', 'none')
            .attr('stroke-width', 1)
            .attr('d', line)
            .attr('clip-path', `url(#${clipId})`)
            .style('pointer-events', 'none');
        });
    },

    attachEventHandlers(selection) {
      const self = this;

      selection
        .on('mouseenter', function () {
          if (!self.selectedTaskId) {
            d3.select(this).style('opacity', 1);
          }
        })
        .on('mouseleave', function () {
          const element = d3.select(this);

          if (!self.selectedTaskId || element.attr('data-id') !== self.selectedTaskId) {
            element.style('opacity', 0.75);
          }
        })
        .on('click', function () {
          // In D3 context, 'this' refers to the DOM element
          const taskId = d3.select(this).attr('data-id');
          const taskData = self.timeData.find((item) => item.id === taskId);

          if (!taskData) {
            console.warn('Task data not found for ID:', taskId);

            return;
          }

          const rect = this.getBoundingClientRect();
          const position = self.calculateTaskPosition(taskData);

          self.$emit('task-clicked', {
            taskId,
            taskData,
            position: {
              ...position,
              rect: {
                x: rect.left + rect.width / 2,
                y: rect.top,
                width: rect.width,
                height: rect.height,
                bottom: rect.bottom,
              },
            },
          });
        });
    },

    applySelectionState() {
      const svg = d3.select(this.$refs.timelineSvg);

      // Reset all elements
      svg.selectAll('.row-activity, .activity-area-shape').classed('selected', false).style('opacity', 0.75);

      // Apply selection if task is selected
      if (this.selectedTaskId) {
        svg.selectAll('.row-activity, .activity-area-shape').style('opacity', 0.5);

        svg.selectAll(`[data-id="${this.selectedTaskId}"]`).classed('selected', true).style('opacity', 1);
      }
    },

    getYPosition(type) {
      switch (type) {
        case 'meetings':
          return CHART_CONFIG.Y_OFFSET_MEETING;
        case 'shifts':
          return CHART_CONFIG.Y_OFFSET_SHIFTS;
        default:
          return CHART_CONFIG.Y_OFFSET_DEFAULT;
      }
    },

    getHeight(type) {
      switch (type) {
        case 'meetings':
          return CHART_CONFIG.MEETING_HEIGHT;
        case 'shifts':
          return CHART_CONFIG.SHIFTS_HEIGHT;
        default:
          return CHART_CONFIG.BAR_HEIGHT;
      }
    },

    getRadius(type) {
      return type === 'meetings' ? CHART_CONFIG.MEETING_RADIUS : CHART_CONFIG.BAR_RADIUS;
    },

    calculateTaskPosition(taskData) {
      const start = d3.isoParse(taskData.start_date);
      const end = d3.isoParse(taskData.end_date);
      const dayStart = this.today.toDate();
      const totalDayMs = 24 * 60 * 60 * 1000;
      const startPercent = ((start - dayStart) / totalDayMs) * 100;
      const endPercent = ((end - dayStart) / totalDayMs) * 100;
      const widthPercent = endPercent - startPercent;

      return { startPercent, endPercent, widthPercent };
    },

    hasMeetingOverlap(meeting, xScale) {
      const meetingStart = xScale(meeting.start);
      const meetingEnd = xScale(meeting.end);

      return this.processedTimeData.some(task => {
        if (task.id === meeting.id || task.type === 'shifts') {
          return false;
        }

        const taskStart = xScale(task.start);
        const taskEnd = xScale(task.end);

        return !(meetingEnd <= taskStart || meetingStart >= taskEnd);
      });
    },
  },
};
</script>
